import { InputAdornment, TextFieldProps, Tooltip } from '@mui/material'
import { LocalizedParseFloat } from 'components/Common/Input/NumberParts'
import { NumberFormatCustom } from 'components/Common/NumberFormatInputComponent'
import { TextFieldWithLoading } from 'components/TextFieldWithLoading'
import { projectOperationSelector } from 'features/BillOfMaterials/store/selectors/projectOperationPendingSelector'
import { isEqual } from 'lodash'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumericFormatProps } from 'react-number-format'
import { MoneyDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import {
  RootAppState,
  useAppDispatch,
  useAppSelector,
} from 'store/configureStore'
import { Key } from 'utils/keyCodes'

type Props = {
  /**
   * selector to get the value ((state: RootAppState) => (string | number) | Money | Quantity)
   */
  valueSelector?: (state: RootAppState) => (string | number) | MoneyDto
  /**
   * a function created with createAsyncFunc
   */
  saveAction
  /**
   * if a value is provided, it will be used as the default value (will not go to redux store)
   */
  value?: (string | number) | MoneyDto
  textFieldProps?: TextFieldProps
  actionTypePrefix?: string
  validation?: (value: string | number) => {
    isValid: boolean
    errorMessage?: string
  }
  disableShowHelpText?: boolean
  money?: boolean
  textAlign?: 'left' | 'center' | 'right'
  allowNegative?: boolean
  helperTextFunc?: (value: string) => string
  disableShowTooltipForLongText?: boolean
}

/**
 * given a redux selector or a value (but not both) and an async save action
 * this component should be able to be used as a TextField
 * supporting money and quantity
 * @param props
 * @returns
 */
export const ProjectTextField = (props: Props) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const valueFromStore = useAppSelector((state) => {
    if (props.value) {
      return props.value
    }

    if (props.valueSelector) {
      return props.valueSelector(state)
    }

    return null
  }, isEqual)

  const operationStatus = useAppSelector(
    projectOperationSelector(
      props.saveAction['typePrefix'] || props.actionTypePrefix,
    ),
  )

  const isEditing = useRef(false)
  const [value, setValue] = useState(undefined)
  const [validation, setValidation] = useState<{
    isValid: boolean
    errorMessage?: string
  }>(undefined)
  const [tooltipOpen, setTooltipOpen] = useState(false)

  useEffect(() => {
    if (!validation?.isValid === false) {
      setTooltipOpen(true)
    }
  }, [validation])

  const _setValue = (value: null | string | number | MoneyDto) => {
    if (
      Boolean(value) &&
      typeof value === 'object' &&
      Object.keys(value || {}).includes('value')
    ) {
      setValue(value.value)
    } else {
      setValue(value)
    }
  }

  useEffect(() => {
    if (!isEditing.current) {
      _setValue(valueFromStore)
    }

    return () => {
      isEditing.current = false
    }
  }, [valueFromStore])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    isEditing.current = true
    setValue(event.target.value)
    if (props.validation) {
      setValidation(props.validation(event.target.value))
    }
  }

  const fieldIsRequiredAndHasValue = () => {
    if (props.textFieldProps?.required) {
      return !!value
    }

    return true
  }

  const handleSave = (valueToSave: string) => {
    let valueIsEqual = false

    if (typeof valueFromStore === 'number' && isNaN(Number(valueToSave))) {
      // for quantities, you should not try to save a not a number.
      return
    }

    const parsedValue = LocalizedParseFloat(valueToSave, 0)

    if (
      Boolean(valueFromStore) &&
      typeof valueFromStore === 'object' &&
      'value' in valueFromStore
    ) {
      // Quantity or Money
      valueIsEqual =
        LocalizedParseFloat(valueFromStore.value?.toString()) ===
        LocalizedParseFloat(valueToSave?.toString())
    } else {
      valueIsEqual = isEqual(
        valueFromStore?.toString(),
        valueToSave?.toString(),
      )
    }

    if (
      fieldIsRequiredAndHasValue() &&
      (validation === undefined || validation.isValid === true) &&
      !valueIsEqual
    ) {
      if (valueFromStore !== null && props?.money) {
        dispatch(
          props.saveAction({
            ...(valueFromStore as MoneyDto),
            value: parsedValue,
          } as MoneyDto),
        )
      } else {
        dispatch(props.saveAction(valueToSave))
      }
      isEditing.current = false
    }
  }

  const saveOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      (!props.textFieldProps?.multiline && e.key === Key.Enter) ||
      (props.textFieldProps?.multiline &&
        e.key === Key.Enter &&
        (e.ctrlKey || e.metaKey))
    ) {
      handleSave(e.currentTarget.value ?? e.target['value'])
      return
    }

    if (e.key === Key.Escape) {
      _setValue(valueFromStore)
      setValidation(undefined)
      isEditing.current = false
      return
    }
  }

  const lastStatusShown = useRef(undefined)
  useEffect(() => {
    if (
      lastStatusShown.current !== operationStatus?.requestId &&
      operationStatus?.requestStatus === 'rejected'
    ) {
      ShowException('project', operationStatus.error)
      lastStatusShown.current = operationStatus.requestId
    }
  }, [operationStatus])

  const moneyInputProps = props?.money && {
    inputComponent: NumberFormatCustom as never,
    inputProps: {
      style: { textAlign: props.textAlign || 'right', paddingRight: '8px' },
      allowNegative: props.allowNegative || false,
      allowLeadingZeros: false,
      thousandSeparator: false,
    } as NumericFormatProps,
  }

  const StartAdornment = useCallback(() => {
    if (valueFromStore && valueFromStore['symbol']) {
      return (
        <InputAdornment position="start">
          {valueFromStore['symbol']}
        </InputAdornment>
      )
    }

    return null
  }, [valueFromStore])

  const helperText =
    (props.textFieldProps?.required && !value && t('common:required')) ||
    validation?.errorMessage // ||

  const inputRef = React.useRef<HTMLInputElement>(null)
  useEffect(() => {
    const wheelHandler = (e: WheelEvent) => {
      e.preventDefault()
    }

    if (props.textFieldProps?.type === 'number') {
      const input = inputRef?.current
      if (input) {
        input.addEventListener('wheel', wheelHandler)
      }

      return () => {
        input?.removeEventListener('wheel', wheelHandler)
      }
    }

    return () => null
  }, [props.textFieldProps.type])

  if (value == undefined) {
    return null
  }

  return (
    <Tooltip
      title={
        Boolean(helperText)
          ? helperText
          : value?.toString().length > 25 &&
              !props.disableShowTooltipForLongText
            ? value
            : ''
      }
      open={tooltipOpen}
      onOpen={() => setTooltipOpen(true)}
      // onClose={() => setTooltipOpen(false)}
      arrow
    >
      <TextFieldWithLoading
        {...(props.textFieldProps || {})}
        loading={
          operationStatus ? operationStatus?.requestStatus === 'pending' : null
        }
        loadingVariant={'endAdornment'}
        autoComplete="new-password"
        title={undefined}
        ref={inputRef}
        value={value ?? ''}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onBlur={(e) => handleSave(e.currentTarget.value)}
        onFocus={(e) => e.target.select()}
        onChange={handleChange}
        onKeyDown={saveOnEnter}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => {
          if (!validation || validation.isValid) {
            setTooltipOpen(false)
          }
        }}
        helperText={props.helperTextFunc?.(value)}
        error={
          (props.textFieldProps?.required && !value) ||
          validation?.isValid === false
        }
        slotProps={{
          ...props.textFieldProps.slotProps,
          input: {
            startAdornment: <StartAdornment />,
            ...(moneyInputProps || {}),
          },
        }}
      />
    </Tooltip>
  )
}
